/**
 * Synthetic patient fixture schema (PROD-579).
 *
 * Phase 1 ships 4 canonical synthetic patient profiles per ADR-PROTO-002.
 * Every fixture validates against the Zod schema below; invalid data fails
 * at module load via the registry's parse step (see ./index.ts).
 *
 * Risk-tier vocabulary is the master prompt v3.3 set
 * (`manual_approve` | `review_window` | `auto_execute`) per SPEC.md §7.1
 * and the ADR-001 Risk Tier Action Map. NBA action types must match the
 * map; the schema accepts the set of action types listed in the master
 * prompt §ADR-001 Risk Tier Action Map (Inline).
 *
 * Per HC1 + ADR-PROTO-002: every name surfacing in clinician-facing UI
 * carries the `[SYNTHETIC]` suffix - that rendering is the responsibility
 * of the consumer (PROD-569 PatientNameTag); fixtures store plain names.
 *
 * No real PII anywhere (mitigation 4): labs, MRN, vitals, demographics,
 * insurer names are all synthetic. The names are the ONE concession.
 *
 * Per HC5: no em dashes in any fixture string. Use `-` (hyphen),
 * comma, or "and" / "to" instead.
 */
import { z } from 'zod';

// Risk tier vocabulary (ADR-001 Risk Tier Action Map per master prompt v3.3).
export const riskTierSchema = z.enum([
  'manual_approve',
  'review_window',
  'auto_execute',
]);
export type RiskTier = z.infer<typeof riskTierSchema>;

// Action types from master prompt v3.3 §ADR-001 Risk Tier Action Map.
// New action types require an ADR-001 amendment; the schema rejects
// anything outside this closed set.
export const actionTypeSchema = z.enum([
  'send_educational_message',
  'schedule_followup',
  'dispatch_PROM',
  'order_lab',
  'draft_prescription',
  'modify_pathway_step',
  'trigger_referral',
  'record_diagnosis_code',
  'update_medication_list',
  'flag_red_flag',
  // [IMPLEMENTED][PROD-583] Reverted `present_pathway_view` to restore
  // ADR-001 Layer 2 conformance (closed enum, no governance bypass).
  // Phase 2 follow-up will file a proper ADR-001 amendment when concrete
  // PathwayView UX lands; until then NBA #3 is classified as a
  // `send_educational_message` (manual_approve + clinician_review_required
  // fences are preserved by each fixture).
]);
export type ActionType = z.infer<typeof actionTypeSchema>;

// Clinical pathway identifiers (Brand Patch 2026-05-06: replaces the
// legacy TLP1-TLP4 surface vocabulary with slug-cased clinical names).
export const pathwaySchema = z.enum([
  'cardiometabolic-pathway',
  'hrt-pathway',
  'metabolic-syndrome-pathway',
  'cvd-prevention-pathway',
]);
export type Pathway = z.infer<typeof pathwaySchema>;

// Tenant ids match useTenantStore (state/types.ts).
export const tenantIdSchema = z.enum([
  'apex-cardiometabolic',
  'longevity-health-institute',
]);
export type TenantId = z.infer<typeof tenantIdSchema>;

// ICD-10 surface form. Loose pattern that admits 3-character codes
// (e.g., E66) and dotted decimals (e.g., E66.01, I10).
export const icd10Schema = z.string().regex(/^[A-Z]\d{2}(\.\d{1,2})?$/);

// Ethnic subgroup (Wave 4 P0.2). Phase 0 lands the type and the Zod
// extension; existing fixtures continue to validate without populating
// this field. Wave 4 P1 sub-agents will populate values via overlay
// fixtures, which is why the field is `nullable().optional()`.
//
// The closed enum is informed by the canonical citation registry seed:
//   - south_asian       -> Misra/Khurana JCEM 2008, QRISK3 South Asian adj.
//   - east_asian        -> WHO 2004 Asian-Pacific BMI cutoffs
//   - arab_mena         -> Al-Lawati 2003, Malik 2008 UAE national survey,
//                          Baniissa 2022 UAE cross-sectional, MENA GBD 2019
//   - african           -> ACC/AHA PCE African-American risk band
//   - european          -> SCORE2 European calibration (default)
//   - mixed             -> documented mixed ancestry; overlay logic
//                          applies the dominant-component rule and flags
//                          for clinician confirmation
//   - not_specified     -> field exists but no clinical value recorded
//   - not_disclosed     -> patient declined; do NOT default-impute an
//                          ethnicity overlay; surface the gap to the UI
//
// New values require a registry-corresponding citation and a Wave 4 P1
// supervisor decision; sub-agents must not freelance new buckets.
export const ethnicSubgroupSchema = z.enum([
  'south_asian',
  'east_asian',
  'arab_mena',
  'african',
  'european',
  'mixed',
  'not_specified',
  'not_disclosed',
]);
export type EthnicSubgroup = z.infer<typeof ethnicSubgroupSchema>;

// Citation shape. HC3 mandates a visible source on every NBA. The id is
// a short tag (e.g., `ADA-2024-Standards`); the title + year + url are
// surfaced verbatim in the citation footer.
export const citationSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  year: z.number().int().min(1990).max(2100),
  url: z.string().url().optional(),
});
export type Citation = z.infer<typeof citationSchema>;

// Validation tag vocabulary aligned with the dispatch's CI gate. Tags
// surface UAE-overlay uncertainty markers in the NBA card UI per Wave 5
// Phase A. Optional on the base NBA schema so non-overlay fixtures
// (Wave-3 baseline walkthroughs) do not need a tag and the card renders
// no badge for them, per the Wave 5 Phase A "do not fabricate" rule.
export const nbaValidationTagSchema = z.enum([
  '[IMPLEMENTED]',
  '[PLANNED]',
  '[UNVERIFIED]',
  '[CONTEXTUAL EVIDENCE]',
]);
export type NbaValidationTag = z.infer<typeof nbaValidationTagSchema>;

// Single NBA payload per master prompt §LLM Output Schema (Zod-shape).
// Strings carry verbatim content from HTML-BEHAVIORAL-EXTRACTION §3 with
// em dashes rewritten per HC5.
export const nbaSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  rationale: z.string().min(1).max(400),
  confidence: z.number().min(0).max(1),
  riskTier: riskTierSchema,
  actionType: actionTypeSchema,
  ctaVerb: z.enum(['Order', 'Dispatch', 'Present', 'Defer']),
  cited_sources: z.array(citationSchema).min(1),
  clinician_review_required: z.boolean(),
  red_flags_detected: z.array(z.string()),
  // UI string from HTML behavioral extraction §3 (e.g.,
  // "Accepted 96% of the time at this clinic").
  learn_signal: z.string().min(1),
  // Optional UAE-overlay validation tag (Wave 5 Phase A). Surfaces the
  // uncertainty marker on the NBA card via `<ValidationTagBadge>`. When
  // absent, the card renders no badge.
  validationTag: nbaValidationTagSchema.optional(),
});
export type Nba = z.infer<typeof nbaSchema>;

// Full chart shape - mirrors the chart cards in the HTML ClinicalDesk
// left rail (parity audit row 4 / behavioral extraction §1) but stored
// as plain typed records here. Phase 2 ClinicalDesk renders these.
export const chartSchema = z.object({
  problemList: z.array(z.string().min(1)).min(5),
  medications: z.array(z.string().min(1)),
  vitals: z.object({
    bmi: z.number().positive(),
    heightCm: z.number().positive(),
    weightKg: z.number().positive(),
    bloodPressure: z.string().min(1),
    heartRate: z.number().positive(),
    temperatureC: z.number().positive(),
  }),
  lastVisit: z.object({
    date: z.string().min(1),
    summary: z.string().min(1),
  }),
});
export type Chart = z.infer<typeof chartSchema>;

// Lab panel - keys are LOINC-shaped names; values carry numeric result
// + unit + reference range. Strict typing keeps Phase 2 consumers safe.
export const labResultSchema = z.object({
  value: z.number(),
  unit: z.string().min(1),
  referenceRange: z.string().min(1),
  // Synthetic timestamp; ISO date.
  collectedAt: z.string().min(1),
});
export const labsSchema = z.object({
  HbA1c: labResultSchema,
  LDL: labResultSchema,
  ApoB: labResultSchema,
  eGFR: labResultSchema,
  hsCRP: labResultSchema,
  // Per-protocol additional labs typed as optional.
  triglycerides: labResultSchema.optional(),
  HDL: labResultSchema.optional(),
  fastingGlucose: labResultSchema.optional(),
  fastingInsulin: labResultSchema.optional(),
  TSH: labResultSchema.optional(),
  estradiol: labResultSchema.optional(),
  FSH: labResultSchema.optional(),
  Lp_a: labResultSchema.optional(),
  CK: labResultSchema.optional(),
});
export type Labs = z.infer<typeof labsSchema>;

// SOAP draft - one verbatim string per section. Phase 2 ClinicalDesk
// types these via the typewriter cascade (HTML behavioral extraction §4.1).
export const soapDraftSchema = z.object({
  cc: z.string().min(1),
  hpi: z.string().min(1),
  assessment: z.string().min(1),
  plan: z.string().min(1),
});
export type SoapDraft = z.infer<typeof soapDraftSchema>;

// Top-level patient fixture.
export const patientFixtureSchema = z.object({
  id: z.string().min(1).regex(/^[a-z]+(-[a-z]+)+$/),
  name: z.string().min(1),
  age: z.number().int().min(0).max(120),
  sex: z.enum(['M', 'F']),
  tenant: tenantIdSchema,
  pathway: pathwaySchema,
  // Pre-configured clinical question that pre-populates the Chief
  // Complaint zone when this patient is selected (PP-4 acceptance test).
  presetClinicalQuestion: z.string().min(1),
  // Two-line summary surfaced in the picker row.
  summary: z.string().min(1),
  chart: chartSchema,
  labs: labsSchema,
  icd10: z.array(icd10Schema).min(1),
  // Always exactly 3 NBAs per HTML behavioral extraction §3.
  nbas: z.array(nbaSchema).length(3),
  soapDraft: soapDraftSchema,
  // Wave 4 P0.2: optional ethnic subgroup. Existing fixtures omit; P1
  // sub-agents populate via overlay fixtures. `null` permitted to
  // distinguish "field exists, no value" from "field absent".
  ethnic_subgroup: ethnicSubgroupSchema.nullable().optional(),
  // Sprint 4.6 (PROD-657): Ramadan-observant flag for UAE-overlay
  // conditional rendering. Absent means false (non-observant default).
  ramadan_observant: z.boolean().optional(),
  // Sprint 4.6 (PROD-657): Fasting medical exemption per IDF-DAR risk
  // stratification. Only meaningful when ramadan_observant is true.
  fasting_medical_exemption: z.boolean().optional(),
  // Sprint 4.6 (PROD-657): Halal eating preference flag. Triggers the
  // halal-and-prayer-time overlay when set to true.
  halal_eating: z.boolean().optional(),
  // Sprint 4.6 (PROD-657): Prayer-time observant flag. Triggers the
  // halal-and-prayer-time overlay when set to true.
  prayer_time_observant: z.boolean().optional(),
});
export type PatientFixture = z.infer<typeof patientFixtureSchema>;
